import { Component, computed, inject } from '@angular/core';
import { IonApp, IonRouterOutlet } from '@ionic/angular/standalone';
import { AppSettingsService } from './core/services/app-settings.service';
import { ScanService } from './core/services/scan.service';
import { ScanOverlayComponent } from './shared/components/scan-overlay/scan-overlay.component';
import { ScreenOrientation } from '@capacitor/screen-orientation';
import { PlatformService } from './core/services/platform.service';

/**
 * Root application shell component.
 *
 * @remarks
 * Wraps the entire app in `<ion-app>` (required by Ionic for modals, alerts,
 * and toast positioning) with `<ion-router-outlet>` as the navigation host.
 * All UI lives inside feature components loaded by the router.
 *
 * A single `<app-scan-overlay>` is mounted here so the camera lifecycle is
 * global — only one overlay instance ever exists regardless of which page is
 * active. `BrandHeaderComponent` calls `ScanService.openScan()` to open it.
 */
@Component({
  selector: 'app-root',
  imports: [IonApp, IonRouterOutlet, ScanOverlayComponent],
  template: `
    <ion-app>
      @if (showSplash()) {
        <!-- First-run-only splash (2026-07-29) — see AppSettingsService's
             initialLoadSettled doc comment. A returning device with a
             populated app_settings_cache never sees this: settings() is
             already non-null immediately from restore(), before this
             component even renders. Text-based wordmark, not an image —
             this exists specifically to cover a slow/cold network on the
             very first launch, so it must not itself depend on the network
             (the brand header's own logo is a remote image; deliberately
             not reused here for that reason). -->
        <div class="app-splash">
          <div class="app-splash__logo">
            <span class="app-splash__logo-main">TITANIUM</span>
            <span class="app-splash__logo-sub">LEGAL</span>
          </div>
          <p class="app-splash__loading">Loading…</p>
        </div>
      } @else {
        <!-- animated=false: prevents .ion-page-invisible from getting stranded
             when the WAAPI onfinish callback fails to fire (observed when navigating
             between root-level routes from inside an ion-tabs shell). -->
        <ion-router-outlet [animated]="false" />
        <!-- Scan overlay AFTER ion-router-outlet so DOM order reinforces the
             z-index:99999 on :host when Ionic's stacking contexts compete. -->
        @if (scanSvc.scanOpen()) {
          <app-scan-overlay
            hint="Point camera at a work-order barcode"
            (scanned)="scanSvc.onScanResult($event)"
            (closed)="scanSvc.onScanClosed($event)"
          />
        }
      }
    </ion-app>
  `,
  styles: [
    `
      .app-splash {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        height: 100%;
        width: 100%;
        background: #ffffff;
        gap: 24px;
      }
      .app-splash__logo {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 2px;
      }
      .app-splash__logo-main {
        font-family: var(--font-heading, 'Montserrat', sans-serif);
        font-size: 28px;
        font-weight: 700;
        letter-spacing: 0.04em;
        color: var(--ion-color-primary, #164c04);
      }
      .app-splash__logo-sub {
        font-family: var(--font-body, 'Manrope', sans-serif);
        font-size: 13px;
        font-weight: 500;
        letter-spacing: 0.2em;
        color: #6a8a62;
      }
      .app-splash__loading {
        margin: 0;
        font-family: var(--font-body, 'Manrope', sans-serif);
        font-size: 14px;
        color: #888888;
      }
    `,
  ],
})
export class App {
  /** Exposed to the template so the `@if` and event bindings can reach it. */
  protected readonly scanSvc = inject(ScanService);

  private readonly appSettingsSvc = inject(AppSettingsService);

  private readonly platform = inject(PlatformService);

  /**
   * `true` only during the narrow window on a brand-new install where no
   * cached settings exist yet and the very first `GET /settings/app` fetch
   * hasn't settled — see `AppSettingsService.initialLoadSettled`'s doc
   * comment for the full context (this is the "exceptional case" left over
   * once app bootstrap stopped blocking on that fetch).
   */
  protected readonly showSplash = computed(
    () => !this.appSettingsSvc.settings() && !this.appSettingsSvc.initialLoadSettled(),
  );

  ngOnInit(): void {
    if (this.platform.isNative) {
      void this.lockNativePortrait();
      return;
    }

    if (this.platform.isWeb) {
      this.setupWebOrientationCheck();
    }
  }

  private async lockNativePortrait(): Promise<void> {
    try {
      await ScreenOrientation.lock({
        orientation: 'portrait',
      });
    } catch (error) {
      console.warn('Unable to lock screen orientation:', error);
    }
  }

  private setupWebOrientationCheck(): void {
    this.updateWebOrientation();

    screen.orientation?.addEventListener('change', () => {
      this.updateWebOrientation();
    });
  }

  private updateWebOrientation(): void {
    const overlay = document.getElementById('orientation-lock-overlay');

    if (!overlay) {
      return;
    }

    const isMobileWeb = this.isMobileWeb();

    const isLandscape = screen.orientation?.type?.startsWith('landscape') ?? false;

    overlay.style.display = isMobileWeb && isLandscape ? 'flex' : 'none';
  }

  private isMobileWeb(): boolean {
    const userAgent = navigator.userAgent.toLowerCase();

    return /android|iphone|ipad|ipod|mobile/.test(userAgent);
  }
}
